import { render } from 'preact';
import qrcode from 'qrcode-generator';
import { t } from '../i18n/index.js';
import { joinUrl, PUBLIC_ORIGIN } from '../joinLinks.js';
import type { InviteKind } from '../../shared/protocol.js';

/** QR code as SVG (with the standard 4-module quiet zone). */
export function QrCode(props: { text: string; class?: string; label?: string }) {
  const qr = qrcode(0, 'M');
  qr.addData(props.text);
  qr.make();
  const n = qr.getModuleCount();
  let d = '';
  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      if (qr.isDark(row, col)) d += `M${col + 4} ${row + 4}h1v1h-1z`;
    }
  }
  return (
    <svg class={props.class} viewBox={`0 0 ${n + 8} ${n + 8}`} shape-rendering="crispEdges" role="img" aria-label={props.label}>
      <rect width={n + 8} height={n + 8} fill="#fff" />
      <path d={d} fill="#0d1220" />
    </svg>
  );
}

/** Colour logo for paper: the same emblem as on the site, with a dark "A" on white. */
export function PrintLogo() {
  return (
    <svg class="card-logo" viewBox="0 0 230 48" aria-label={t.appName}>
      <defs>
        <linearGradient id="print-ring" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#1fa8ea" />
          <stop offset="1" stop-color="#7c5cf0" />
        </linearGradient>
        <linearGradient id="print-word" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#1fa8ea" />
          <stop offset="0.55" stop-color="#7c5cf0" />
          <stop offset="1" stop-color="#d946ef" />
        </linearGradient>
      </defs>
      <path d="M30.5 5.2A20 20 0 1 1 17.5 5.2" fill="none" stroke="url(#print-ring)" stroke-width="4" stroke-linecap="round" />
      <path d="M24 9 L13 37 M24 9 L35 37" stroke="#16213a" stroke-width="4" stroke-linecap="round" fill="none" />
      <path d="M24 22.5l1.6 3.4 3.7.4-2.8 2.5.8 3.7-3.3-1.9-3.3 1.9.8-3.7-2.8-2.5 3.7-.4z" fill="#f5b400" />
      <text x="58" y="35" fill="url(#print-word)" font-size="30" font-weight="800" letter-spacing="6" font-family="system-ui, 'Segoe UI', Roboto, sans-serif">
        AREENA
      </text>
    </svg>
  );
}

export interface InviteCard {
  code: string;
  expiresAt: number;
  /** Guest codes have their own texts and lead to the guest page (decision 2026-09-29). */
  kind: CardKind;
  /** Bingo codes: the batch label, shown instead of an expiry date. */
  note?: string;
}

/** Registration and guest codes, and bingo codes (decision 2026-09-29). */
export type CardKind = InviteKind | 'bingo';

export const CARDS_PER_PAGE = 8;

/** "ABCD2345" → "ABCD 2345" for reading; the registration form accepts both. */
export const spaced = (code: string) => `${code.slice(0, 4)} ${code.slice(4)}`;

/** The slogans rotate over the cards in order (the same on paper and in saved files). */
export const sloganFor = (index: number, kind: CardKind = 'register') =>
  kind === 'guest'
    ? t.admin.invites.guestCard.slogan
    : kind === 'bingo'
      ? t.admin.bingo.card.slogan
      : t.admin.invites.slogans[index % t.admin.invites.slogans.length]!;

/** Texts and QR code target of a card. */
export const cardTexts = (kind: CardKind) =>
  kind === 'guest' ? t.admin.invites.guestCard : kind === 'bingo' ? t.admin.bingo.card : t.admin.invites.card;
/** The bottom line: validity, or the batch of a bingo code. */
export const cardValidText = (card: InviteCard) =>
  card.kind === 'bingo'
    ? t.admin.bingo.card.valid
    : (card.kind === 'guest' ? t.admin.invites.guestCard : t.admin.invites.card).valid(new Date(card.expiresAt).toLocaleDateString('fi-FI'));
/** The address printed on the card: bingo codes are used on the bingo page (fix 2026-09-30). */
export const cardSite = (kind: CardKind) => {
  const host = PUBLIC_ORIGIN.replace(/^https?:\/\//, '');
  return kind === 'bingo' ? `${host}/bingo` : host;
};
export const cardUrl = (card: InviteCard) =>
  card.kind === 'bingo' ? `${PUBLIC_ORIGIN}/bingo/#${card.code}` : joinUrl(card.kind === 'guest' ? 'guest' : 'code', card.code);

function InviteCardsSheet(props: { cards: InviteCard[]; withQr: boolean }) {
  const pages: InviteCard[][] = [];
  for (let i = 0; i < props.cards.length; i += CARDS_PER_PAGE) pages.push(props.cards.slice(i, i + CARDS_PER_PAGE));
  return (
    <>
      {pages.map((page, p) => (
        <div class="print-page" key={p}>
          {page.map((card, i) => (
            <div class={`invite-card ${props.withQr ? 'with-qr' : ''}`} key={card.code}>
              <div class="card-stripe" />
              <div class="card-body">
                <PrintLogo />
                <p class="card-heading">{cardTexts(card.kind).heading}</p>
                <p class="card-slogan">{sloganFor(p * CARDS_PER_PAGE + i, card.kind)}</p>
                <div class="card-code">
                  <span>{cardTexts(card.kind).code}</span>
                  <strong>{spaced(card.code)}</strong>
                </div>
                <p class="card-register">
                  {cardTexts(card.kind).register} <b>{cardSite(card.kind)}</b>
                  {props.withQr && <span class="card-scan">{cardTexts(card.kind).scan}</span>}
                </p>
                <p class="card-valid">
                  {cardValidText(card)}
                </p>
              </div>
              {props.withQr && <QrCode class="card-qr" text={cardUrl(card)} />}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/**
 * Prints invite cards (8 per A4, cut along the dashed lines). The sheet is rendered outside the
 * application; while printing only the sheet is visible (styles.css, @media print).
 */
export function printInviteCards(cards: InviteCard[], withQr: boolean) {
  const host = document.createElement('div');
  host.className = 'print-sheet';
  document.body.appendChild(host);
  render(<InviteCardsSheet cards={cards} withQr={withQr} />, host);
  document.body.classList.add('printing');
  const cleanup = () => {
    removeEventListener('afterprint', cleanup);
    document.body.classList.remove('printing');
    render(null, host);
    host.remove();
  };
  addEventListener('afterprint', cleanup);
  // Let the browser lay out the sheet before the print dialog opens.
  requestAnimationFrame(() => setTimeout(() => print(), 50));
}
